import antlr4 from 'antlr4';
import moment from 'moment-timezone';
import validators from 'validator';

import { DateFilterModifier, FilterTypes } from '../types';
import { SearchSyntaxLexer } from './SearchSyntaxLexer';
import { ExpressionContext, SearchSyntaxParser } from './SearchSyntaxParser';

export function deserialize(searchFilterString: string): FilterTypes[] {
  const chars = new antlr4.InputStream(searchFilterString);
  const lexer = new SearchSyntaxLexer(chars);
  const tokens = new antlr4.CommonTokenStream(lexer);
  const parser = new SearchSyntaxParser(tokens);
  const tree = parser.query();
  const visitor = new Visitor();

  return visitor.visitQuery(tree);
}

class Visitor {
  visitQuery(expr: any): FilterTypes[] {
    const filters: FilterTypes[] = [];

    for (let i = 0; i < expr.children.length; i++) {
      const node = expr.children[i];
      if (node instanceof ExpressionContext) {
        const filter = this.visitExpression(node);
        if (filter) {
          filters.push(filter);
        }
      }
    }

    return filters;
  }

  visitExpression(expr: any) {
    const negated = !!expr.NEGATION();

    if (expr.field()) {
      const expression = this.visitField(expr.field());
      return expression
        ? {
            ...expression,
            negated,
          }
        : undefined;
    } else {
      let value;

      if (expr.string()) {
        value = this.visitString(expr.string());
      } else if (expr.word()) {
        value = this.visitWord(expr.word());
      } else if (expr.ip()) {
        value = this.visitIp(expr.ip());
      }

      return { negated, operator: 'keyword', value };
    }
  }

  visitField(expr: any) {
    let filter;

    if (expr.fieldMatchValue()) {
      filter = this.visitFieldMatchValue(expr.fieldMatchValue());
    } else if (expr.fieldComparison()) {
      filter = this.visitFieldComparison(expr.fieldComparison());
    } else if (expr.fieldMatchRange()) {
      filter = this.visitFieldMatchRange(expr.fieldMatchRange());
    } else if (expr.fieldMatchNull()) {
      filter = this.visitFieldMatchNull(expr.fieldMatchNull());
    } else if (expr.fieldMatchFunction()) {
      filter = this.visitFieldMatchFunction(expr.fieldMatchFunction());
    }

    return filter;
  }

  visitFieldComparison(expr: any) {
    const value = this.visitComparable(expr.comparable());
    const comparator = expr.COMPARATOR().getText();
    const operator = {
      '>': 'greaterThan',
      '<': 'lessThan',
      '>=': 'greaterThanOrEqual',
      '<=': 'lessThanOrEqual',
    }[comparator];

    return {
      prop: this.visitFieldMatch(expr.fieldMatch()),
      value,
      operator,
    };
  }

  visitFieldMatch(expr: any) {
    const str = expr.FIELD_MATCH().getText();
    return str.substring(0, str.length - 1);
  }

  visitFieldMatchValue(expr: any) {
    const prop = this.visitFieldMatch(expr.fieldMatch());
    const value = this.visitValue(expr.value());
    let operator = 'equals';

    if (typeof value === 'string') {
      if (prop === 'tag') {
        operator = 'containKeyValue';
      } else if (validators.isIPRange(value)) {
        operator = 'isInIpRange';
      }
    }

    return {
      operator,
      prop,
      value,
    };
  }

  visitFieldMatchRange(expr: any) {
    return {
      prop: this.visitFieldMatch(expr.fieldMatch()),
      operator: 'range',
      start: this.visitComparable(expr.children[1]),
      end: this.visitComparable(expr.children[3]),
    };
  }

  visitFieldMatchNull(expr: any) {
    return {
      prop: this.visitFieldMatch(expr.fieldMatch()),
      operator: 'equals',
      value: null,
    };
  }

  visitFieldMatchFunction(expr: any) {
    const fn = this.visitWord(expr.functionName);
    const prop = this.visitFieldMatch(expr.fieldMatch());
    const args = expr.args.map((arg) => this.visitFieldMatchFunctionArg(arg));

    if (fn === 'in') {
      return {
        prop,
        operator: 'in',
        value: args,
      };
    } else if (fn === 'contains') {
      return {
        prop,
        operator: 'contains',
        value: args[0],
      };
    } else if (fn === 'matchesRegex') {
      return {
        prop,
        operator: 'matchesRegex',
        value: args[0],
      };
    } else if (fn === 'matchesWildcard') {
      return {
        prop,
        operator: 'matchesWildcard',
        value: args[0],
      };
    }
  }

  visitFieldMatchFunctionArg(expr: any) {
    if (expr.word()) {
      return this.visitWord(expr.word());
    } else if (expr.string()) {
      return this.visitString(expr.string());
    } else if (expr.ip()) {
      return this.visitIp(expr.ip());
    }
  }

  visitValue(expr: any) {
    if (expr.number()) {
      return this.visitNumber(expr.number());
    } else if (expr.word()) {
      return this.visitWord(expr.word());
    } else if (expr.string()) {
      return this.visitString(expr.string());
    } else if (expr.ip()) {
      return this.visitIp(expr.ip());
    } else if (expr.date()) {
      return this.visitDate(expr.date());
    }
  }

  visitComparable(expr: any) {
    if (expr.number()) {
      return this.visitNumber(expr.number());
    } else if (expr.dateTimeExpr()) {
      return this.visitDateTimeExpr(expr.dateTimeExpr());
    } else if (expr.date()) {
      return {
        value: this.visitDate(expr.date()),
        modifiers: [],
      };
    } else if (expr.string()) {
      return this.visitString(expr.string());
    }
  }

  visitNumber(expr: any) {
    return Number(expr.getText());
  }

  visitString(expr: any) {
    const text = expr.getText();
    return text.substring(1, text.length - 1);
  }

  visitWord(expr: any) {
    return expr.getText();
  }

  visitIp(expr: any) {
    return expr.getText();
  }

  visitDate(expr: any) {
    const dateText = expr.DATE().getText();
    const timeZoneText = expr.TIMEZONE() ? expr.TIMEZONE().getText() : 'Z';
    return moment(dateText + 'T00:00:00' + timeZoneText).toDate();
  }

  visitDateTimeExpr(expr: any) {
    const value = this.visitDateTime(expr.dateTime());
    let round;
    const modifiers: DateFilterModifier[] = [];

    for (const child of expr.children.slice(1)) {
      const parsedChild = this.visitDateTimeOperation(child);
      if (parsedChild.unit) {
        modifiers.push(parsedChild);
      } else {
        round = parsedChild;
      }
    }

    return {
      value,
      round,
      modifiers,
    };
  }

  visitDateTime(expr: any) {
    if (expr.DATETIME()) {
      return moment.utc(expr.DATETIME().getText()).toDate();
    }
    if (expr.DATETIME_FN()) {
      return expr.DATETIME_FN().getText();
    }
  }

  visitDateTimeOperation(expr: any) {
    if (expr.dateTimeRound()) {
      return this.visitDateTimeRound(expr.dateTimeRound());
    } else if (expr.dateTimeMath()) {
      return this.visitDateTimeMath(expr.dateTimeMath());
    } else {
      return (date) => date;
    }
  }

  visitDateTimeRound(expr: any) {
    return expr.DATETIME_UNIT().getText();
  }

  visitDateTimeMath(expr: any) {
    const text = expr.DATETIME_MATH().getText();
    const operator = text[0];
    const unit = text.slice(text.length - 1);
    const value =
      Number(text.slice(1, text.length - 1)) * (operator === '-' ? -1 : 1);

    return {
      unit,
      value,
    };
  }
}
